import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Suitcase, Plus, ArrowCounterClockwise, Trash, Check,
  ArrowLeft, FloppyDisk, Target,
} from "@phosphor-icons/react";

const CATEGORIES = ["wood", "hybrid", "iron", "wedge", "putter"];
const CAT_COLOR = {
  wood:   "#0F2537",
  hybrid: "#1E5688",
  iron:   "#E05D3A",
  wedge:  "#FFC72C",
  putter: "#1E5631",
};

export default function MyBag() {
  const [clubs, setClubs] = useState([]);
  const [isDefault, setIsDefault] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Pro: real GPS-measured averages per club. Pulled from /stats/clubs and
  // keyed by club name so the bag row can show "Measured 245y (8 shots)" and
  // offer a one-tap "Use measured" button to overwrite the stated distance.
  const [measured, setMeasured] = useState({});

  useEffect(() => {
    api.get("/users/me/bag")
      .then((r) => { setClubs(r.data.clubs); setIsDefault(r.data.is_default); })
      .finally(() => setLoading(false));
    // Measured averages — only available to Pro users; silently ignored otherwise.
    api.get("/stats/clubs")
      .then((r) => {
        const m = {};
        for (const c of r.data || []) m[c.club] = c;
        setMeasured(m);
      })
      .catch(() => {});  // non-Pro returns 402/403 — fall through quietly
  }, []);

  const useMeasured = (i, club, avg) => {
    setClub(i, { distance_yards: avg });
    toast.success(`${club} → ${avg} yds (measured)`);
  };
  const applyAllMeasured = () => {
    let n = 0;
    setClubs((curr) => curr.map((c) => {
      const m = measured[c.club];
      if (m && m.avg_yards && m.avg_yards !== c.distance_yards) {
        n += 1;
        return { ...c, distance_yards: m.avg_yards };
      }
      return c;
    }));
    if (n) {
      setDirty(true);
      toast.success(`Updated ${n} club${n === 1 ? "" : "s"} to measured averages`);
    } else {
      toast.info("All clubs already match your measured averages");
    }
  };

  const setClub = (i, patch) => {
    setClubs((curr) => curr.map((c, idx) => idx === i ? { ...c, ...patch } : c));
    setDirty(true);
  };
  const addClub = () => {
    setClubs((curr) => [...curr, { club: "New club", distance_yards: 150, category: "iron" }]);
    setDirty(true);
  };
  const removeClub = (i) => {
    setClubs((curr) => curr.filter((_, idx) => idx !== i));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/users/me/bag", { clubs });
      setClubs(r.data.clubs);
      setIsDefault(false);
      setDirty(false);
      toast.success("Bag saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset to the standard 14-club bag? Your edits will be lost.")) return;
    setSaving(true);
    try {
      const r = await api.post("/users/me/bag/reset");
      setClubs(r.data.clubs);
      setIsDefault(false);
      setDirty(false);
      toast.success("Bag reset to default");
    } catch {
      toast.error("Reset failed");
    } finally { setSaving(false); }
  };

  // Sorted view (descending by distance, putter last) for a clean ladder.
  const sorted = useMemo(() => {
    const withIdx = clubs.map((c, idx) => ({ ...c, _idx: idx }));
    return withIdx.sort((a, b) => {
      if (a.category === "putter") return 1;
      if (b.category === "putter") return -1;
      return (b.distance_yards || 0) - (a.distance_yards || 0);
    });
  }, [clubs]);

  const maxDist = Math.max(120, ...clubs.map((c) => c.distance_yards || 0));

  if (loading) return <div className="py-10">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Link to="/profile" data-testid="back-to-profile" className="inline-flex items-center gap-1 text-sm font-bold text-[#0F2537]/70 hover:text-[#0F2537]">
          <ArrowLeft size={14} weight="bold" /> Profile
        </Link>
        <Link to="/club-bag" className="inline-flex items-center gap-1 text-sm font-bold text-[#E05D3A] hover:underline" data-testid="link-to-pro-bag">
          <Target size={14} weight="bold" /> See GPS-measured averages (Pro) →
        </Link>
      </div>

      <div className="rise flex items-start justify-between gap-3 flex-wrap">
        <div>
          <span className="label-eyebrow inline-flex items-center gap-1"><Suitcase weight="fill" size={14} /> My bag</span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">14 clubs.</h1>
          <p className="text-[#0F2537]/70 mt-2 max-w-2xl">
            Tell us what you carry and how far you hit it. We use these distances for on-course club suggestions and to pre-fill the Shot Tracker.
            {isDefault && <span className="block mt-1 text-xs italic">You're seeing the default bag — save once to make it yours.</span>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {Object.keys(measured).length > 0 && (
            <button onClick={applyAllMeasured} data-testid="apply-all-measured-btn"
              className="px-3 py-2 rounded-xl border-2 border-[#E05D3A] text-[#E05D3A] font-bold text-sm inline-flex items-center gap-1 hover:bg-[#E05D3A]/5"
              title="Update every club's distance to your measured GPS average">
              <Target size={14} weight="bold" /> Use measured (×{Object.keys(measured).length})
            </button>
          )}
          <button onClick={addClub} data-testid="add-club-btn" className="px-3 py-2 rounded-xl border border-[#E2E0D8] text-[#0F2537] font-bold text-sm inline-flex items-center gap-1">
            <Plus size={14} weight="bold" /> Add
          </button>
          <button onClick={reset} disabled={saving} data-testid="reset-bag-btn" className="px-3 py-2 rounded-xl border border-[#E2E0D8] text-[#0F2537]/70 font-bold text-sm inline-flex items-center gap-1 hover:text-[#0F2537] disabled:opacity-60">
            <ArrowCounterClockwise size={14} /> Reset
          </button>
          <button onClick={save} disabled={saving || !dirty} data-testid="save-bag-btn" className="px-4 py-2 rounded-xl bg-[#E05D3A] text-white font-bold text-sm inline-flex items-center gap-1 hover:brightness-110 disabled:opacity-60">
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : dirty ? "Save bag" : "Saved"}
          </button>
        </div>
      </div>

      <div className="card-default overflow-hidden" data-testid="bag-list">
        {sorted.map((c) => {
          const i = c._idx;
          const pct = c.distance_yards ? (c.distance_yards / maxDist) * 100 : 0;
          const color = CAT_COLOR[c.category] || "#0F2537";
          return (
            <div key={i} className="grid grid-cols-12 gap-2 px-4 py-3 items-center border-b border-[#E2E0D8] last:border-0" data-testid={`bag-row-${i}`}>
              <div className="col-span-12 sm:col-span-3">
                <input value={c.club} onChange={(e) => setClub(i, { club: e.target.value })} data-testid={`bag-club-name-${i}`} maxLength={30}
                  className="w-full px-3 py-2 rounded-lg border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537] font-heading font-bold text-sm" />
              </div>
              <div className="col-span-6 sm:col-span-2">
                <select value={c.category || "iron"} onChange={(e) => setClub(i, { category: e.target.value })} data-testid={`bag-category-${i}`}
                  className="w-full px-3 py-2 rounded-lg border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537] text-sm">
                  {CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div className="col-span-6 sm:col-span-2">
                <div className="relative">
                  <input type="number" min={0} max={400} value={c.distance_yards} onChange={(e) => setClub(i, { distance_yards: Math.max(0, Math.min(400, parseInt(e.target.value || "0", 10))) })}
                    data-testid={`bag-distance-${i}`}
                    className="w-full px-3 py-2 pr-10 rounded-lg border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537] text-right font-bold" />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#0F2537]/50 uppercase tracking-wider">yd</span>
                </div>
              </div>
              <div className="col-span-10 sm:col-span-4">
                <div className="relative h-3 rounded-full bg-[#0F2537]/5 overflow-hidden">
                  <div className="absolute h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                </div>
              </div>
              <div className="col-span-2 sm:col-span-1 text-right">
                <button onClick={() => removeClub(i)} data-testid={`bag-remove-${i}`} className="text-[#0F2537]/40 hover:text-red-600 p-1">
                  <Trash size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
